import { LocateFixed, Search } from 'lucide-react';
import { useCallback, useState, type FormEvent } from 'react';
import { usePolling } from '../../hooks/usePolling';
import { endpoints } from '../../services/api';
import { startMicrosoft, useSession } from '../../store/session';
import { currentPosition, searchPlaces } from '../../services/weather';
import { useSettings, type Location } from '../../store/settings';
import { ZOOM_MAX, ZOOM_MIN, zoomLabel } from '../../lib/tiles';
import { buttonClass, Choice, ghostButtonClass, inputClass, Section, Toggle } from './controls';

const ZONES: string[] = (() => {
  try {
    return (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.('timeZone') ?? [];
  } catch {
    return [];
  }
})();

/** Which Microsoft To Do lists appear as tabs in the widget. */
function TodoListChooser() {
  const hidden = useSettings((s) => s.lists.microsoftHidden);
  const update = useSettings((s) => s.update);
  const { data, loading, error } = usePolling('todo-lists-settings', useCallback(() => endpoints.todoLists().then((r) => r.lists), []), 10 * 60_000);
  if (!data) return <p className="muted text-xs">{loading ? 'Loading your To Do lists…' : error}</p>;
  return (
    <ul className="space-y-1.5 pl-1">
      {data.map((l) => (
        <li key={l.id}>
          <label className="flex cursor-pointer items-center gap-3 text-sm">
            <input
              type="checkbox"
              checked={!hidden.includes(l.id)}
              onChange={() =>
                update('lists', { microsoftHidden: hidden.includes(l.id) ? hidden.filter((h) => h !== l.id) : [...hidden, l.id] })
              }
              className="accent-[var(--accent)]"
            />
            {l.name}
          </label>
        </li>
      ))}
    </ul>
  );
}

function ZoomSlider({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <label className="block">
      <div className="mb-1 flex justify-between text-sm">
        <span>{label}</span>
        <span className="muted">
          {zoomLabel(value)} ({value})
        </span>
      </div>
      <input
        type="range"
        min={ZOOM_MIN}
        max={ZOOM_MAX}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-[var(--accent)]"
      />
    </label>
  );
}

/** Where and when the photo on screen was taken, with a map. */
function PhotoInfoSection() {
  const info = useSettings((s) => s.photoInfo);
  const update = useSettings((s) => s.update);
  return (
    <Section
      title="Photo location & date"
      description="Where and when the photo on screen was taken, with a map. It appears for photos that carry this information: OneDrive photos and photos added from this device."
    >
      <Toggle label="Show photo location & date" checked={info.enabled} onChange={(enabled) => update('photoInfo', { enabled })} />
      {info.enabled && (
        <>
          <Toggle label="Town or city" checked={info.showCity} onChange={(showCity) => update('photoInfo', { showCity })} />
          <Toggle
            label="County or district"
            checked={info.showDistrict}
            onChange={(showDistrict) => update('photoInfo', { showDistrict })}
          />
          <Toggle label="Country" checked={info.showCountry} onChange={(showCountry) => update('photoInfo', { showCountry })} />
          <Toggle label="Date taken" checked={info.showDate} onChange={(showDate) => update('photoInfo', { showDate })} />
          <Toggle label="Time taken" checked={info.showTime} onChange={(showTime) => update('photoInfo', { showTime })} />
          <Toggle label="Map" checked={info.showMap} onChange={(showMap) => update('photoInfo', { showMap })} />
          {info.showMap && (
            <>
              <Choice
                options={[
                  { id: 'fixed', name: 'Fixed zoom', description: 'The map stays at the zoom you choose' },
                  { id: 'gradual', name: 'Zoom in slowly', description: 'From a wide view in to your zoom while the photo is shown' },
                ]}
                value={info.zoomMode}
                onChange={(zoomMode) => update('photoInfo', { zoomMode })}
              />
              {info.zoomMode === 'gradual' && (
                <ZoomSlider
                  label="Start from"
                  value={info.zoomFrom}
                  onChange={(zoomFrom) => update('photoInfo', { zoomFrom, zoom: Math.max(info.zoom, zoomFrom) })}
                />
              )}
              <ZoomSlider
                label={info.zoomMode === 'gradual' ? 'Zoom in to' : 'Zoom'}
                value={info.zoom}
                onChange={(zoom) =>
                  update('photoInfo', info.zoomMode === 'gradual' ? { zoom, zoomFrom: Math.min(info.zoomFrom, zoom) } : { zoom })
                }
              />
            </>
          )}
        </>
      )}
    </Section>
  );
}

export function WidgetsTab() {
  const { config, microsoft } = useSession();
  const clock = useSettings((s) => s.clock);
  const photosOnly = useSettings((s) => s.photosOnly);
  const weather = useSettings((s) => s.weather);
  const lists = useSettings((s) => s.lists);
  const update = useSettings((s) => s.update);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Location[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const search = async (e: FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const r = await searchPlaces(query.trim());
      setResults(r);
      if (!r.length) setError('No places found.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setBusy(false);
    }
  };

  const locate = async () => {
    setBusy(true);
    setError(null);
    try {
      update('weather', { location: await currentPosition() });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not get location');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Section title="Photos only" description="Show just your photos and videos – no clock, weather, calendar, lists or news. Your widget settings are kept for when you switch back.">
        <Toggle label="Photos only" checked={photosOnly} onChange={(v) => update('photosOnly', v)} />
      </Section>
      <Section title="Clock">
        <Toggle label="Show clock" checked={clock.enabled} onChange={(enabled) => update('clock', { enabled })} />
        <Toggle label="12-hour clock" checked={clock.hour12} onChange={(hour12) => update('clock', { hour12 })} />
        <Toggle label="Show seconds" checked={clock.showSeconds} onChange={(showSeconds) => update('clock', { showSeconds })} />
        <Toggle label="Show date" checked={clock.showDate} onChange={(showDate) => update('clock', { showDate })} />
        <label className="block text-sm">
          <span className="mb-1 block">Time zone</span>
          <select
            className={`${inputClass} bg-[var(--surface-strong)]`}
            value={clock.timeZone}
            onChange={(e) => update('clock', { timeZone: e.target.value })}
          >
            <option value="">This device ({Intl.DateTimeFormat().resolvedOptions().timeZone})</option>
            {ZONES.map((z) => (
              <option key={z} value={z}>
                {z.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
        </label>
      </Section>

      <Section title="Weather" description="7-day forecast from Open-Meteo.">
        <Toggle label="Show weather" checked={weather.enabled} onChange={(enabled) => update('weather', { enabled })} />
        <Toggle
          label="Fahrenheit"
          checked={weather.unit === 'fahrenheit'}
          onChange={(f) => update('weather', { unit: f ? 'fahrenheit' : 'celsius' })}
        />
        <Toggle label="Hourly forecast" checked={weather.showHourly} onChange={(showHourly) => update('weather', { showHourly })} />
        <Toggle label="7-day forecast" checked={weather.showDaily} onChange={(showDaily) => update('weather', { showDaily })} />
        <Toggle
          label="Weather warnings (official, via MeteoAlarm – Europe)"
          checked={weather.showWarnings}
          onChange={(showWarnings) => update('weather', { showWarnings })}
        />
        {weather.showWarnings && weather.location && !weather.location.countryCode && (
          <p className="muted text-xs">Warnings need a searched location – search for your town below.</p>
        )}
        <Toggle label="Feels-like temperature" checked={weather.showFeelsLike} onChange={(showFeelsLike) => update('weather', { showFeelsLike })} />
        <Toggle label="Chance of rain" checked={weather.showRain} onChange={(showRain) => update('weather', { showRain })} />
        <Toggle label="Wind" checked={weather.showWind} onChange={(showWind) => update('weather', { showWind })} />
        {weather.showWind && (
          <Choice
            options={[
              { id: 'kmh', name: 'km/h' },
              { id: 'mph', name: 'mph' },
              { id: 'ms', name: 'm/s' },
            ]}
            value={weather.windUnit}
            onChange={(windUnit) => update('weather', { windUnit })}
            columns={3}
          />
        )}
        <Toggle label="Humidity" checked={weather.showHumidity} onChange={(showHumidity) => update('weather', { showHumidity })} />
        <Toggle label="UV index" checked={weather.showUv} onChange={(showUv) => update('weather', { showUv })} />
        <Toggle label="Sunrise & sunset" checked={weather.showSun} onChange={(showSun) => update('weather', { showSun })} />
        <p className="text-sm">
          Location: <strong>{weather.location?.name ?? 'not set'}</strong>
        </p>
        <form onSubmit={search} className="flex gap-2">
          <input
            className={inputClass}
            placeholder="Search a town or city"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search location"
          />
          <button className={buttonClass} disabled={busy} type="submit" aria-label="Search">
            <Search className="h-4 w-4" aria-hidden />
          </button>
          <button className={ghostButtonClass} disabled={busy} type="button" onClick={locate} aria-label="Use my location">
            <LocateFixed className="h-4 w-4" aria-hidden />
          </button>
        </form>
        {error && <p className="text-sm text-red-400">{error}</p>}
        {results.length > 0 && (
          <ul className="space-y-1">
            {results.map((r) => (
              <li key={`${r.latitude},${r.longitude}`}>
                <button
                  className="w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-[var(--border)]"
                  onClick={() => {
                    update('weather', { location: r });
                    setResults([]);
                    setQuery('');
                  }}
                >
                  {r.name}
                </button>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="To-do & shopping lists">
        <Toggle label="Show lists" checked={lists.enabled} onChange={(enabled) => update('lists', { enabled })} />
        {config?.features.microsoft && (
          <Choice
            options={[
              { id: 'local', name: 'KCL Canvas lists', description: 'To-do + shopping, synced between screens' },
              { id: 'microsoft', name: 'Microsoft To Do', description: 'Your lists from the linked account' },
            ]}
            value={lists.source}
            onChange={(source) => update('lists', { source })}
          />
        )}
        {lists.source === 'microsoft' && config?.features.microsoft ? (
          microsoft?.connected && microsoft.tasks ? (
            <TodoListChooser />
          ) : (
            <div className="flex items-center justify-between gap-3 text-sm">
              <span className="muted">
                {microsoft?.connected ? 'KCL Canvas needs permission to read your To Do lists.' : 'Connect your Microsoft account first.'}
              </span>
              <button className="shrink-0 text-[var(--accent)] hover:underline" onClick={() => startMicrosoft('link')}>
                {microsoft?.connected ? 'Reconnect Microsoft' : 'Connect Microsoft'}
              </button>
            </div>
          )
        ) : (
          <p className="muted text-xs">Lists sync between screens when you’re signed in.</p>
        )}
      </Section>

      <PhotoInfoSection />
    </>
  );
}
